import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  inject,
  OnDestroy,
  viewChild,
} from '@angular/core';
import {
  BbitGeoObject,
  BbitGeoObjectType,
  IBbitGeoCoordinates,
  IBbitMapOptions,
  IBbitMarkerGeoObject,
  IBbitPolygonGeoObject,
} from '@bbitgmbh/bbit.utils';
// biome-ignore lint/suspicious/noShadowRestrictedNames: required
import { Feature, Map, MapBrowserEvent, View } from 'ol';
import { Attribution } from 'ol/control';
import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import {
  DoubleClickZoom,
  DragPan,
  DragRotate,
  DragZoom,
  KeyboardPan,
  KeyboardZoom,
  Modify,
  MouseWheelZoom,
  PinchRotate,
  PinchZoom,
  Select,
} from 'ol/interaction';
import { ModifyEvent } from 'ol/interaction/Modify';
import { Tile } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { OSM } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import tinycolor from 'tinycolor2';
import { BbitMapHelper } from './helper';

// // custom key interaction
// class BbitOlKeyboardEvent extends Interaction {
//   constructor(
//     private _options: IBbitMapOptions,
//     private _bbitFeatureGeoObjectKey: string,
//     private _select: Select,
//   ) {
//     super();
//   }
//   override handleEvent(mapBrowserEvent: MapBrowserEvent<KeyboardEvent>) {
//     let stopEvent = false;
//     if (mapBrowserEvent.type === 'keydown') {
//       const keyEvent = mapBrowserEvent.originalEvent;
//       if (keyEvent.code?.toLowerCase() === 'escape') {
//         this._select.getFeatures().clear();
//         stopEvent = true;
//       }
//       if (keyEvent.code?.toLowerCase() === 'backspace' || keyEvent.code?.toLowerCase() === 'delete') {
//         const selected = this._select.getFeatures();
//         selected.forEach((feature) => {
//           const object = feature.get(this._bbitFeatureGeoObjectKey);
//           if (object) {
//             if (this._options?.onObjectDeleted) {
//               this._options.onObjectDeleted(object);
//             }
//           }
//         });
//         stopEvent = true;
//       }
//       if (stopEvent) {
//         keyEvent.preventDefault();
//       }
//     }
//     return !stopEvent;
//   }
// }

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnDestroy, AfterViewInit {
  private _cdr = inject(ChangeDetectorRef);

  readonly mapElem = viewChild<ElementRef>('map');
  @Input()
  set options(options: IBbitMapOptions) {
    this._initialize(options);
    this._options = options;
  }
  private _options!: IBbitMapOptions;
  private _previousOptions!: IBbitMapOptions;
  private _map!: Map;
  private _mapView!: View;
  private _osmLayer!: Tile<OSM>;
  private _objectsSource!: VectorSource<Feature<Geometry>>;
  private _objectsLayer!: VectorLayer<VectorSource, Feature<Geometry>>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: required
  private _cache = new BehaviorSubject<{ [id: string]: { object: BbitGeoObject; feature: Feature<any> } }>({});
  private _bbitFeatureGeoObjectKey!: 'bbitObject';
  private _ngUnsubscribe = new Subject<void>();
  private _center = new BehaviorSubject<IBbitGeoCoordinates | undefined>(undefined);
  private _zoom = new BehaviorSubject<number | undefined>(undefined);
  private _isMapInitialized = false;
  private _modifyEnd = new Subject<ModifyEvent>();
  private _moveEnd = new Subject<void>();
  private _optionsChanged = new Subject<void>();
  private _select!: Select;
  private _modify!: Modify;
  private _preventInitialPositionChange = true;

  ngOnDestroy(): void {
    this._optionsChanged.next();
    this._optionsChanged.unsubscribe();
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  ngAfterViewInit(): void {
    // this is a dirty hack because angular 12 is currently not able to import the ol css
    const css = document.createElement('link');
    css.href = './assets/ol/ol.css';
    css.rel = 'stylesheet';
    this.mapElem()?.nativeElement.appendChild(css);

    this._initialize(this._options);
  }

  private _initialize(options: IBbitMapOptions): void {
    const mapElem = this.mapElem();
    if (!options || !mapElem?.nativeElement || options === this._previousOptions) {
      return;
    }

    if (!this._isMapInitialized) {
      // create required components

      // initialize map view
      this._mapView = new View({
        center: undefined, // BbitMapHelper.toOlCoordinates({ lng: 7.45141793478979, lat: 46.948200053840516 }),
        zoom: undefined,
        maxZoom: 20,
        minZoom: 2,
      });

      // initialize OpenStreetMaps layer
      this._osmLayer = new TileLayer({
        source: new OSM(),
      });

      // initialize source and layer for drawn objects
      this._objectsSource = new VectorSource({ wrapX: false });
      this._objectsLayer = new VectorLayer({
        source: this._objectsSource,
      });
      // initialize select handler
      this._select = new Select({
        style: (feature) => {
          const object: BbitGeoObject = feature.get(this._bbitFeatureGeoObjectKey);
          const color = this._options?.selectColor || object.selectColor || '#DA8100';
          switch (object.type) {
            case BbitGeoObjectType.MARKER:
              return this._getMarkerStyle(Object.assign({}, object, { color }));
            case BbitGeoObjectType.POLYGON:
              return this._getPolygonStyle(Object.assign({}, object, { color }));
          }
        },
        hitTolerance: 10,
      });

      // initialize modify handler
      this._modify = new Modify({
        // features: select.getFeatures(),
        source: this._objectsSource,
      });

      this._modify.on('modifyend', (event) => {
        this._modifyEnd.next(event);
      });

      this._map = new Map({
        target: mapElem.nativeElement,
        controls: [new Attribution()],
        layers: [this._osmLayer, this._objectsLayer],
        interactions: [
          // defaults
          new DragRotate(),
          new DoubleClickZoom(),
          new DragPan(),
          new PinchRotate(),
          new PinchZoom(),
          new KeyboardPan(),
          new KeyboardZoom(),
          new MouseWheelZoom(),
          new DragZoom(),
          // custom interactions added if draw is enabled
        ],
        view: this._mapView,
      });

      this._zoom.pipe(takeUntil(this._ngUnsubscribe)).subscribe((zoom) => {
        if (zoom) {
          this._mapView.setZoom(zoom);
        }
      });

      this._center.pipe(takeUntil(this._ngUnsubscribe)).subscribe((center) => {
        if (center) {
          this._mapView.setCenter(BbitMapHelper.toOlCoordinates(center));
        }
      });

      // click listeners
      this._map.on('singleclick', (event) => {
        this.mapElem()?.nativeElement.focus();
        const feature = this._getFirstFeatureFromClickEvent(event);
        if (feature) {
          const object: BbitGeoObject = feature.get(this._bbitFeatureGeoObjectKey);
          if (object.onClick) {
            object.onClick();
            event.preventDefault();
          }
        } else {
          // clear features
          this._select.getFeatures().clear();
          if (options?.onMapClick) {
            options.onMapClick(BbitMapHelper.toBbitCoordinates(event.coordinate));
            event.preventDefault();
          }
        }
      });

      this._map.on('dblclick', (event) => {
        const feature = this._getFirstFeatureFromClickEvent(event);
        if (feature) {
          const object: BbitGeoObject = feature.get(this._bbitFeatureGeoObjectKey);
          if (object.onClick) {
            object?.onDoubleClick?.();
            event.preventDefault();
          }
        } else {
          if (options?.onMapDoubleClick) {
            options.onMapDoubleClick(BbitMapHelper.toBbitCoordinates(event.coordinate));
            event.preventDefault();
          }
        }
      });

      // move and zoom listeners
      this._map.on('moveend', () => {
        this._moveEnd.next();
      });

      this._isMapInitialized = true;
    }

    // trigger unsubscribe on option subjects
    this._optionsChanged.next();

    if (options?.center) {
      options.center.pipe(takeUntil(this._optionsChanged)).subscribe(this._center);
    }

    if (options.zoom) {
      options.zoom.pipe(takeUntil(this._optionsChanged)).subscribe(this._zoom);
    }

    if (options?.centerMapBasedOnObjects) {
      options.centerMapBasedOnObjects.pipe(debounceTime(100), takeUntil(this._optionsChanged)).subscribe(() => {
        this._centerMapBasedOnObjects();
      });
    }

    if (options?.onObjectChanged) {
      this._modifyEnd.pipe(takeUntil(this._optionsChanged)).subscribe((event) => {
        event.features.forEach((feature) => {
          const object: BbitGeoObject = feature.get(this._bbitFeatureGeoObjectKey);
          switch (object.type) {
            case BbitGeoObjectType.MARKER:
              options?.onObjectChanged?.(this._markerFromFeature(feature as Feature<Point>, object));
              break;
            case BbitGeoObjectType.POLYGON:
              options?.onObjectChanged?.(this._polygonFromFeature(feature as Feature<Polygon>, object));
              break;
          }
        });
      });
    }

    // render objects :D
    options?.objects?.pipe(takeUntil(this._optionsChanged)).subscribe((objects) => {
      const coords: IBbitGeoCoordinates[] = [];
      this._select.getFeatures().clear();
      for (const object of objects) {
        switch (object.type) {
          case BbitGeoObjectType.MARKER: {
            let marker: Feature<Point>;
            if (this._cache.value[object._id]) {
              this._cache.value[object._id].object = object;
              marker = this._cache.value[object._id].feature;
            } else {
              marker = new Feature(new Point(BbitMapHelper.toOlCoordinates(object.coordinates)));
              this._objectsSource.addFeature(marker);
              this._cache.value[object._id] = { object, feature: marker };
            }
            marker.set(this._bbitFeatureGeoObjectKey, object);
            marker.getGeometry()?.setCoordinates(BbitMapHelper.toOlCoordinates(object.coordinates));
            marker.setStyle(this._getMarkerStyle(object));
            coords.push(object.coordinates);
            break;
          }
          case BbitGeoObjectType.POLYGON: {
            let polygon: Feature<Polygon>;
            const polygonCoordinates = [];
            for (const o of object.coordinates) {
              polygonCoordinates.push(BbitMapHelper.toOlCoordinates(o));
            }
            if (this._cache.value[object._id]) {
              this._cache.value[object._id].object = object;
              polygon = this._cache.value[object._id].feature;
            } else {
              polygon = new Feature(new Polygon([polygonCoordinates]));
              this._objectsSource.addFeature(polygon);
              this._cache.value[object._id] = { object, feature: polygon };
            }
            polygon.set(this._bbitFeatureGeoObjectKey, object);
            polygon.getGeometry()?.setCoordinates([polygonCoordinates]);
            polygon.setStyle(this._getPolygonStyle(object));
            // biome-ignore lint/suspicious/noExplicitAny: required
            coords.push(...(polygonCoordinates as any));
            break;
          }
          // default:
          //   throw new Error(`Rendering of object type ${object.type} is not implemented`);
        }
      }

      // cleanup
      for (const id in this._cache.value) {
        const found = objects.find((obj) => obj._id === id);
        if (!found) {
          const cached = this._cache.value[id];
          if (cached) {
            this._objectsSource.removeFeature(cached.feature);
            delete this._cache.value[id];
          }
        }
      }

      // calculate center and zoom based on objects if not set
      if (!this._center.value || !this._zoom.value) {
        this._centerMapBasedOnObjects();
      }

      this._cache.next(this._cache.value);

      this._centerMapBasedOnObjects();
    });

    if (options?.onMapPositionChanged) {
      this._moveEnd.pipe(debounceTime(250), takeUntil(this._optionsChanged)).subscribe(() => {
        if (this._preventInitialPositionChange) {
          this._preventInitialPositionChange = false;
          return;
        }
        // biome-ignore lint/suspicious/noExplicitAny: required
        const center = BbitMapHelper.toBbitCoordinates(this._mapView.getCenter() as any);
        const zoom = this._mapView.getZoom() || 16;
        if (
          this._zoom.value === zoom &&
          this._center.value?.lat === center?.lat &&
          this._center.value?.lng === center.lng
        ) {
          return;
        }
        options?.onMapPositionChanged?.({ center, zoom });
      });
    }

    this._previousOptions = options;

    // this is required since a few subject values for ui rendering are changed
    // and due to angular implementations those changes won't trigger in AfterViewInit
    // setTimeout on updateSize is unfortunately required here due to the flex nature of our outer elements
    this._cdr.detectChanges();
    setTimeout(() => {
      this._map.updateSize();
    }, 0);
  }

  private _centerMapBasedOnObjects() {
    if (this._objectsSource.getFeatures().length > 0) {
      this._mapView.fit(this._objectsSource.getExtent(), {
        size: this._map.getSize(),
        padding: [50, 50, 50, 50],
        maxZoom: 20,
      });
    } else {
      this._center.next(this._center.value || { lat: 46.94811333663731, lng: 7.447791347523447 });
      this._zoom.next(this._zoom.value || 16);
    }
  }

  private _getMarkerStyle(marker?: IBbitMarkerGeoObject): Style {
    return new Style({ image: this._getMarkerImage(marker) });
  }

  private _getMarkerImage(marker?: IBbitMarkerGeoObject): Icon {
    const color = marker?.color || '#18B447';
    const contrastColor = this.getContrastColor(color, undefined, { percentage: 15, invert: true });
    const textColor = this.getContrastColor(contrastColor);
    const label = marker?.label || null;
    const svg = `
    <?xml version="1.0" encoding="UTF-8" standalone="no"?>
    <!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
    <svg width="48" height="48" viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;">
        <g transform="matrix(0.971751,0,0,0.974648,0.677966,0.575763)">
            <path d="M24,0C14.2,0 6.3,7.8 6.3,17.4C6.3,32.9 24,48 24,48C24,48 41.7,32.6 41.7,17.4C41.7,7.8 33.8,0 24,0Z" style="fill:${color};fill-rule:nonzero;stroke:${contrastColor};stroke-width:1px;"/>
        </g>
        <g transform="matrix(1.14286,0,0,1.2,-4,-3.4)">
            <ellipse cx="24.5" cy="17" rx="10.5" ry="10" style="fill:${contrastColor};stroke:${contrastColor};stroke-width:0.85px;stroke-linejoin:round;stroke-miterlimit:1.5;"/>
        </g>
        ${
          label
            ? `<text x="24" y="22.2425" text-anchor="middle" style="font-family:Euclid, sans-serif;font-size:15px;fill:${textColor};">${label}</text>`
            : ''
        }
    </svg>
    `;
    return new Icon({
      opacity: 1,
      src: `data:image/svg+xml;utf8,${encodeURIComponent(svg.trim())}`,
      // scale: 0.5,
      anchorXUnits: 'pixels',
      anchorYUnits: 'pixels',
      anchor: [24, 48],
    });
  }

  private _getPolygonStyle(polygon?: IBbitPolygonGeoObject): Style {
    let color = polygon?.color;
    if (!color) {
      color = '#4798C9';
    }
    return new Style({
      stroke: new Stroke({ color }),
      fill: new Fill({ color: this.getColorWithAlpha(color, polygon?.fillAlpha || 0.1) }),
    });
  }

  private _markerFromFeature(feature: Feature<Point>, marker?: IBbitMarkerGeoObject): IBbitMarkerGeoObject {
    if (!marker) {
      marker = {
        _id: '',
        type: BbitGeoObjectType.MARKER,
        coordinates: { lat: 0, lng: 0 },
      };
    }
    // biome-ignore lint/suspicious/noExplicitAny: required
    marker.coordinates = BbitMapHelper.toBbitCoordinates(feature?.getGeometry()?.getCoordinates() as any);
    return marker;
  }

  private _polygonFromFeature(feature: Feature<Polygon>, polygon?: IBbitPolygonGeoObject): IBbitPolygonGeoObject {
    if (!polygon) {
      polygon = {
        _id: '',
        type: BbitGeoObjectType.POLYGON,
        coordinates: [],
      };
    }
    const coordinates = [];
    for (const o of feature?.getGeometry?.()?.getCoordinates?.()?.[0] || []) {
      coordinates.push(BbitMapHelper.toBbitCoordinates(o));
    }
    polygon.coordinates = coordinates;
    return polygon;
  }

  // biome-ignore lint/suspicious/noExplicitAny: required
  private _getFirstFeatureFromClickEvent(event: MapBrowserEvent<any>): Feature<Geometry> {
    let feature: Feature<Geometry> | undefined;
    this._map.forEachFeatureAtPixel(event.pixel, (o) => {
      if (feature) {
        return;
      }
      feature = o as Feature<Geometry>;
    });
    return feature as unknown as Feature<Geometry>;
  }

  public getContrastColor(
    color: string,
    element?: HTMLElement,
    options?: { percentage: number; invert: boolean },
  ): string {
    color = this.getCssVariableValue(color, element);
    const tc = tinycolor(color);
    if (tc.isDark()) {
      if (options?.invert) {
        return tc.darken(options?.percentage || 100).toString();
      }
      return tc.lighten(options?.percentage || 100).toString();
    }
    if (options?.invert) {
      return tc.lighten(options?.percentage || 100).toString();
    }
    return tc.darken(options?.percentage || 100).toString();
  }

  public getColorWithAlpha(color: string, alpha: number, element?: HTMLElement): string {
    color = this.getCssVariableValue(color, element);
    const tc = tinycolor(color);
    return tc.setAlpha(alpha).toString();
  }

  public getCssVariableValue(key: string, element?: HTMLElement): string {
    if (key.startsWith('var(')) {
      const property = key.substr(4, key.length - 5);
      key = getComputedStyle(element || document.documentElement).getPropertyValue(property);
    }
    return key;
  }
}
